import React, { useState } from 'react';
import AddLiabilityModal from '../../AddLiabilityModal';

interface StepAddLiabilityProps {
  onNext: () => void;
  assetAdded: boolean;
}

const StepAddLiability: React.FC<StepAddLiabilityProps> = ({ onNext, assetAdded }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [liabilitySaved, setLiabilitySaved] = useState(false);

  const subtitle = assetAdded
    ? 'Adding your debts gives you your true net worth — not just one side of the ledger.'
    : 'Track what you owe to see your complete financial picture.';

  return (
    <div className="flex flex-col">
      <h1 className="text-3xl font-bold text-white mb-2">Now, what do you owe?</h1>
      <p className="text-base text-white/50 mb-8">{subtitle}</p>

      {/* Illustrative card — not interactive */}
      <div className="rounded-xl border border-white/10 bg-white/5 p-5 mb-8">
        <p className="text-xs text-white/40 uppercase tracking-widest mb-3">What counts as a liability?</p>
        <ul className="space-y-2">
          {[
            { icon: '🏠', label: 'Mortgage', sub: 'Home or investment property loan' },
            { icon: '💳', label: 'Credit cards', sub: 'Personal or business card balances' },
            { icon: '🚗', label: 'Auto loan', sub: 'Car, truck, or other vehicle financing' },
            { icon: '📋', label: 'Line of credit', sub: 'HELOC, personal, or business LOC' },
            { icon: '🏢', label: 'Business loans', sub: 'Term loans, equipment financing' },
          ].map(({ icon, label, sub }) => (
            <li key={label} className="flex items-center gap-3">
              <span className="text-xl w-7 shrink-0">{icon}</span>
              <div>
                <p className="text-sm font-medium text-white">{label}</p>
                <p className="text-xs text-white/40">{sub}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <AddLiabilityModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        isOnboarding
        onSuccess={() => { setLiabilitySaved(true); setModalOpen(false); }}
      />

      {liabilitySaved ? (
        <>
          <p className="text-sm text-green-400 mb-4">✅ Liability added.</p>
          <div className="flex items-center gap-3 mb-4">
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-xl font-semibold text-base transition-colors"
            >
              Add another liability →
            </button>
            <button
              type="button"
              onClick={onNext}
              className="rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 text-white px-6 py-3 font-medium text-base transition-colors"
            >
              Next step →
            </button>
          </div>
          <p className="text-sm text-white/50">You can add more liabilities any time from the Liabilities page.</p>
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-colors self-start"
          >
            Add a liability →
          </button>
          <button
            type="button"
            onClick={onNext}
            className="text-base text-white/40 hover:text-white transition-colors mt-4 self-start"
          >
            Skip for now
          </button>
        </>
      )}
    </div>
  );
};

export default StepAddLiability;
